/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Animation,
  AnimationType,
  Character,
  GetAnimationByType,
} from "@ictlearningsciences/pal3-core/character";
import { DialogueMessage } from "@ictlearningsciences/pal3-core/types";
import { AudioMode } from "../settings";

export interface IdDialogueMessage extends DialogueMessage {
  id: string;
}

/** Store */
export interface DialogueState {
  messages: IdDialogueMessage[];
  messagesPlayed: IdDialogueMessage[];
  isDockOpen: boolean;
  curMessage?: IdDialogueMessage | undefined;
  curAnimation?: Animation | undefined;
  curSaferNode?: string;
  isPageClicked: boolean;
  isPlaying: boolean;
}

const initialState: DialogueState = {
  messages: [],
  messagesPlayed: [],
  isDockOpen: false,
  isPageClicked: false,
  isPlaying: false,
};

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
function readingTime(text: string): number {
  if (!text) return 0;
  const wpm = 120;
  const words = text?.trim().split(/\s+/).length;
  return 1000 + (words / wpm) * 60 * 1000;
}

export const play = createAsyncThunk(
  "dialogue/play",
  async (args: {
    msg: IdDialogueMessage;
    character: Character;
    audioMode: AudioMode;
    volume: number;
    voice: SpeechSynthesisVoice | null;
  }): Promise<void> => {
    const { msg } = args;
    const readTime = readingTime(msg.audioText || msg.text);
    await delay(readTime);
    return;
  }
);

/** Actions */
export const dialogueSlice = createSlice({
  name: "dialogue",
  initialState,
  reducers: {
    setPageClicked: (state, action: PayloadAction<boolean>) => {
      state.isPageClicked = action.payload;
    },
    setDockOpen: (state, action: PayloadAction<boolean>) => {
      state.isDockOpen = action.payload;
      if (!state.isDockOpen) {
        state.curSaferNode = undefined;
      }
    },
    setSaferNode: (state, action: PayloadAction<string>) => {
      state.curSaferNode = action.payload;
    },
    setAnimation: (state, action: PayloadAction<Animation>) => {
      state.curAnimation = action.payload;
    },
    setCurMessage: (
      state,
      action: PayloadAction<IdDialogueMessage | undefined>
    ) => {
      (document?.getElementById("audio") as HTMLAudioElement)?.pause();
      speechSynthesis?.cancel();
      if (action.payload === undefined) {
        state.curMessage = undefined;
        state.curAnimation = undefined;
      } else {
        state.curMessage = action.payload;
      }
    },

    addMessagesToQueue: (state, action: PayloadAction<IdDialogueMessage[]>) => {
      state.messages = [...state.messages, ...action.payload];
      state.messages.sort((a, b) => {
        if (a.priority === b.priority) {
          return 0;
        }
        if (a.priority === undefined) {
          return 1;
        }
        if (b.priority === undefined) {
          return -1;
        }
        return a.priority - b.priority;
      });
    },
    removeMessageFromQueue: (state) => {
      state.curMessage = state.messages.shift();
    },
    clearMessageQueue: (state) => {
      (document?.getElementById("audio") as HTMLAudioElement)?.pause();
      speechSynthesis?.cancel();
      state.messages = [];
      state.curMessage = undefined;
      state.curAnimation = undefined;
    },
    messagePlayed: (state, action: PayloadAction<IdDialogueMessage>) => {
      state.messagesPlayed = [...state.messagesPlayed, action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(play.pending, (state, action) => {
        const { msg, character, audioMode, volume, voice } = action.meta.arg;
        if (state.curMessage?.text === msg.text || state.isPlaying) {
          return;
        }
        state.curMessage = msg;
        state.curAnimation = GetAnimationByType(
          character,
          msg.animation || AnimationType.talk
        );
        const elements = document.getElementsByTagName("audio");
        for (let i = 0; i < elements.length; i++) {
          elements[i].pause();
        }
        speechSynthesis?.cancel();

        if (
          state.isPageClicked &&
          msg.audioFile &&
          (audioMode === AudioMode.VOICE_ONLY || audioMode === AudioMode.BOTH)
        ) {
          const newAudio = new Audio(
            `${process.env.WWW}/media/audio/${msg.audioFile}.mp3`
          );
          newAudio.volume = volume;
          newAudio.addEventListener("canplay", () => {
            newAudio.play();
          });
        } else if (
          audioMode === AudioMode.TTS_ONLY ||
          audioMode === AudioMode.BOTH
        ) {
          const utterance = new SpeechSynthesisUtterance(
            msg.audioText || msg.text
          );
          if (voice) {
            utterance.voice = voice;
          }
          utterance.volume = volume;
          speechSynthesis.speak(utterance);
        }
        state.isPlaying = true;
      })
      .addCase(play.fulfilled, (state, action) => {
        const elements = document.getElementsByTagName("audio");
        for (let i = 0; i < elements.length; i++) {
          elements[i].pause();
        }
        speechSynthesis?.cancel();
        state.curMessage = undefined;
        // state.curAnimation = undefined;
        state.messagesPlayed = [...state.messagesPlayed, action.meta.arg.msg];
        state.isPlaying = false;
      })
      .addCase(play.rejected, (state, action) => {
        const elements = document.getElementsByTagName("audio");
        for (let i = 0; i < elements.length; i++) {
          elements[i].pause();
        }
        speechSynthesis?.cancel();
        state.curMessage = undefined;
        // state.curAnimation = undefined;
        state.messagesPlayed = [...state.messagesPlayed, action.meta.arg.msg];
        state.isPlaying = false;
      });
  },
});

export const {
  setDockOpen,
  setSaferNode,
  setAnimation,
  setCurMessage,
  addMessagesToQueue,
  removeMessageFromQueue,
  clearMessageQueue,
  messagePlayed,
  setPageClicked,
} = dialogueSlice.actions;
export default dialogueSlice.reducer;
